import type {
  MissionCondition,
  MissionConditionInputReferenceValue,
  MissionConditionPropertyInput
} from '@library/types/missions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetMissionEventDefinitionPropertiesQuery,
  useGetMissionEventDefinitionPropertyQuery,
  useGetMissionEventDefinitionsQuery
} from '@bo-services/promotionsApi';
import { MissionConditionInputSwitch } from '@bo-components/missions/MissionConditionInputSwitch';
import { Select } from '@library/components/selects';
import type { SelectOption } from '@library/types/utils';
import { formatLabelByKey } from '@bo-utils/common';

export type MissionConditionsFormInputsProps = {
  event: MissionCondition;
  index: number;
  onChange: (input: MissionConditionPropertyInput, index: number) => void;
};

export const MissionConditionsFormInputs = React.memo(
  ({ event, index, onChange }: MissionConditionsFormInputsProps) => {
    const [property, setProperty] = useState<string | undefined>(undefined);
    const [condition, setCondition] = useState<string | undefined>(undefined);
    const [referenceValue, setReferenceValue] =
      useState<MissionConditionInputReferenceValue>(undefined);

    const { data: properties, isLoading } =
      useGetMissionEventDefinitionPropertiesQuery({
        event_definition_id: event.event_definition_id
      });
    const { data: eventProperty } = useGetMissionEventDefinitionPropertyQuery(
      {
        event_definition_id: event.event_definition_id,
        property_id: property
      },
      {
        skip: Boolean(!property)
      }
    );
    const { data: definitions } = useGetMissionEventDefinitionsQuery(undefined);
    const currentDef = definitions?.event_definitions.find(
      (def) => def.id === event.event_definition_id
    );

    const propertyOptions = useMemo<SelectOption[]>(() => {
      if (properties) {
        return properties.event_definition_properties.map((p) => ({
          key: p.id,
          name: formatLabelByKey(p.name),
          value: p.id
        }));
      }

      return [];
    }, [properties]);

    const conditionOptions = useMemo<SelectOption[]>(() => {
      if (eventProperty) {
        return eventProperty.event_definition_property.conditions.map((c) => ({
          key: c.name,
          name: c.display_name,
          value: c.name
        }));
      }

      return [];
    }, [eventProperty]);

    const input = useMemo(() => {
      if (property && condition && referenceValue) {
        return {
          challenge_condition_id: event.id,
          event_definition_property_id: property,
          condition,
          reference_value: referenceValue
        };
      }

      return undefined;
    }, [condition, event.id, property, referenceValue]);

    const prevInputRef = useRef<MissionConditionPropertyInput | undefined>(
      undefined
    );

    useEffect(() => {
      if (input && input !== prevInputRef.current) {
        prevInputRef.current = input;
        onChange(input, index);
      }
    }, [index, onChange, input]);

    function handlePropertyChange(value: string) {
      setProperty(value);

      if (value !== property) {
        setCondition(undefined);
        setReferenceValue(undefined);
      }
    }

    if (isLoading) {
      return 'Future home of skeleton form';
    }

    return (
      <div className="flex flex-col gap-y-2">
        {currentDef && (
          <h3 className="text-soClean-100 text-lg leading-6">
            {formatLabelByKey(currentDef.name)}
          </h3>
        )}
        <div className="flex items-center gap-x-4">
          <div className="flex flex-col gap-y-2">
            <label
              htmlFor="0"
              className="text-soClean-100 rounded-md text-xs uppercase leading-6"
            >
              Property
            </label>
            <Select
              className="min-w-[200px]"
              label="Property"
              onChange={(option) => handlePropertyChange(option.value)}
              options={propertyOptions}
              size="small"
              testId="property"
              value={property as string}
            />
          </div>
          {property && eventProperty ? (
            <div className="flex flex-col gap-y-2">
              <label
                htmlFor="0"
                className="text-soClean-100 rounded-md text-xs uppercase leading-6"
              >
                Condition
              </label>
              <Select
                className="min-w-[200px]"
                label="Condition"
                onChange={(option) => setCondition(option.value)}
                options={conditionOptions}
                size="small"
                testId="condition"
                value={condition as string}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-y-2">
              <div className="text-soClean-160/50 rounded-md text-xs uppercase leading-6">
                Condition
              </div>
              <div className="border-soFresh-190/50 h-10 min-w-52 rounded border border-dashed" />
            </div>
          )}
          {condition &&
          eventProperty?.event_definition_property.property_type ? (
            <div className="flex flex-col gap-y-2">
              <label
                htmlFor="referenceValue"
                className="text-soClean-100 rounded-md text-xs uppercase leading-6"
              >
                Reference Value
              </label>
              <MissionConditionInputSwitch
                property={eventProperty.event_definition_property}
                referenceValue={referenceValue}
                onChange={setReferenceValue}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-y-2">
              <div className="text-soClean-160/50 rounded-md text-xs uppercase leading-6">
                Reference Value
              </div>
              <div className="border-soFresh-190/50 h-10 min-w-52 rounded border border-dashed" />
            </div>
          )}
        </div>
      </div>
    );
  }
);
